import { type RefObject, useEffect, useRef } from "react";

import type { RuleGraphNode } from "@intelligems/ig-types/dtos/agentic-logic/rule-graph";
import { useReactFlow, useStoreApi } from "@xyflow/react";

import {
  detailsPanelAnnouncement,
  type RuleGraphViewport,
  viewportClearOfDetailsPanel,
} from "./details-panel";

const PANEL_CAMERA_MS = 200;

/**
 * Renders nothing; keeps the canvas in step with the details panel.
 *
 * - Camera: the panel takes its width out of the canvas, so on open the view
 *   pans (or zooms out) until no node sits behind it, and on close the view
 *   the merchant had before opening is put back. Switching the panel between
 *   nodes keeps that original view to return to.
 * - Announcement: opening and closing are spoken through React Flow's own
 *   live region, naming the step, since the panel appears beside the node
 *   rather than taking focus.
 *
 * A zero-size canvas (hidden tab, jsdom) skips the camera, never the
 * announcement.
 */
export const RuleGraphDetailsPanelSync = ({
  selectedNode,
  canvasRef,
  bounds,
  minZoom,
}: {
  selectedNode: RuleGraphNode | null;
  canvasRef: RefObject<HTMLDivElement | null>;
  bounds: { x: number; y: number; width: number; height: number } | undefined;
  minZoom: number;
}) => {
  const { getInternalNode, getViewport, setViewport } = useReactFlow();
  const store = useStoreApi();
  const previous = useRef<RuleGraphNode | null>(null);
  const viewBeforeOpen = useRef<RuleGraphViewport | null>(null);
  const selectedId = selectedNode?.id ?? null;

  useEffect(() => {
    const before = previous.current;
    previous.current = selectedNode;
    if (before?.id === selectedNode?.id) {
      return;
    }

    if (selectedNode !== null) {
      store.setState({
        ariaLiveMessage: detailsPanelAnnouncement(selectedNode, "opened"),
      });
    } else if (before !== null) {
      store.setState({
        ariaLiveMessage: detailsPanelAnnouncement(before, "closed"),
      });
    }

    const canvas = canvasRef.current?.querySelector<HTMLElement>(".react-flow");
    const canvasSize = {
      width: canvas?.clientWidth ?? 0,
      height: canvas?.clientHeight ?? 0,
    };
    if (canvasSize.width === 0 || canvasSize.height === 0) {
      return;
    }

    if (selectedNode === null) {
      const restore = viewBeforeOpen.current;
      viewBeforeOpen.current = null;
      if (restore !== null) {
        void setViewport(restore, { duration: PANEL_CAMERA_MS });
      }
      return;
    }
    const viewport = getViewport();
    if (viewBeforeOpen.current === null) {
      viewBeforeOpen.current = viewport;
    }
    if (bounds === undefined) {
      return;
    }
    const internal = getInternalNode(selectedNode.id);
    const next = viewportClearOfDetailsPanel({
      viewport,
      bounds,
      canvasSize,
      minZoom,
      selectedNodeRect: internal
        ? {
            ...internal.internals.positionAbsolute,
            width: internal.measured.width ?? internal.width ?? 0,
            height: internal.measured.height ?? internal.height ?? 0,
          }
        : undefined,
    });
    if (next !== viewport) {
      void setViewport(next, { duration: PANEL_CAMERA_MS });
    }
    // Keyed on the selection only: a re-layout while the panel is open must
    // not re-run the camera or re-announce.
  }, [selectedId]);

  return null;
};
